/**
 * Share Management store — servers list, per-server share cache,
 * expanded-row tracking, drive-mapping cross-reference cache, plus
 * permission modal state and the 6-step Share Wizard state machine.
 *
 * Mirrors monolith globals: shareWizardState (16925), expandedServers
 * (16913), window.allServers (19285), window.sharesCache (19288),
 * window.sessionCountCache, window.currentPermissionsPath.
 */

import { create } from 'zustand';
import {
  fetchServers,
  fetchServerSessions,
  fetchSharesList,
  fetchAllDrives
} from '@/api/endpoints/shares.js';

const WIZARD_INITIAL = {
  currentStep: 1,
  serverName: '',
  serverDnsName: '',
  selectedDrive: '',
  currentFolderPath: '',
  folderPath: '',
  shareName: '',
  shareDescription: '',
  ticketNumber: '',
  company: '',
  hiddenShare: false,
  selectedGroups: [],
  newGroups: [],
  groupMembers: {},
  addToDriveMapping: false,
  driveMappingConfig: {}
};

export const useSharesStore = create((set, get) => ({
  servers: [],
  loadingServers: false,
  serversError: null,
  sharesCache: {},                    // serverName → shares[]
  expandedServers: new Set(),
  loadingExpansion: {},               // serverName → bool
  serverSearchQuery: '',

  // drive-mapping cross-reference (cached once)
  driveMappings: [],
  driveMappingsLoaded: false,

  // permissions modal
  permissionsModal: {
    open: false,
    path: null,
    permissions: [],
    loading: false,
    error: null
  },

  // sessions modal
  sessionsModal: {
    open: false,
    server: null,
    sessions: [],
    loading: false,
    error: null
  },

  // share wizard
  wizardOpen: false,
  wizard: { ...WIZARD_INITIAL },

  /* --------- server list --------- */

  async loadServers(forceRefresh = false) {
    set({ loadingServers: true, serversError: null });
    try {
      const data = await fetchServers(forceRefresh);
      const servers = Array.isArray(data) ? data : data?.servers ?? [];
      const sharesCache = {};
      for (const s of servers) {
        if (Array.isArray(s.shares)) sharesCache[s.name] = s.shares;
      }
      set({
        servers,
        sharesCache: { ...get().sharesCache, ...sharesCache },
        loadingServers: false
      });
      // Instant render is done. Pull live online status in the background, then
      // sweep logged-in counts for the servers that come back online.
      get().refreshOnlineStatus().then(() => get().loadSessionCounts());
    } catch (err) {
      set({
        servers: [],
        loadingServers: false,
        serversError: err instanceof Error ? err.message : 'Failed to load servers'
      });
    }
  },

  /**
   * Background sweep: logged-in user count per online server, shown next
   * to the share count on each card. Each query is a remote quser via PS
   * (~seconds each), so run 4 at a time and fill in progressively.
   */
  sessionCounts: {},

  async loadSessionCounts(servers) {
    // Only sweep servers CONFIRMED online (online === true). Excluding null
    // avoids hammering every server with quser when the live probe hasn't
    // resolved yet or the connector is down.
    const targets = (servers || get().servers).filter((s) => s.online === true && s.name);
    const queue = [...targets];
    const worker = async () => {
      while (queue.length) {
        const server = queue.shift();
        try {
          const data = await fetchServerSessions(server.name);
          const count = data?.online === false ? null : (data?.sessions?.length ?? 0);
          set({ sessionCounts: { ...get().sessionCounts, [server.name]: count } });
        } catch {
          set({ sessionCounts: { ...get().sessionCounts, [server.name]: null } });
        }
      }
    };
    await Promise.all(Array.from({ length: 4 }, worker));
  },

  /**
   * Pull live online status for all servers (a batched ?probe=true call) and
   * merge it onto the already-rendered list. Runs after the instant load so the
   * page never blocks on the probes. Failures keep the last-known status.
   */
  async refreshOnlineStatus() {
    try {
      const data = await fetchServers(false, true);
      const list = Array.isArray(data) ? data : data?.servers ?? [];
      const onlineByName = {};
      for (const s of list) onlineByName[String(s.name || '').toLowerCase()] = s.online;
      set((state) => ({
        servers: state.servers.map((s) => {
          const key = String(s.name || '').toLowerCase();
          return key in onlineByName ? { ...s, online: onlineByName[key] } : s;
        })
      }));
    } catch {
      /* keep last-known online */
    }
  },

  setServerSearchQuery(q) {
    set({ serverSearchQuery: q });
  },

  async toggleServerExpansion(serverName) {
    const expanded = new Set(get().expandedServers);
    if (expanded.has(serverName)) {
      expanded.delete(serverName);
      set({ expandedServers: expanded });
      return { collapsed: true };
    }
    // Expanding: load the shares directly. We deliberately do NOT pre-probe a
    // TCP port for "online" — that gave false "Server offline" negatives on
    // boxes reachable over WinRM (so sessions/shares actually work). The share
    // enumeration itself runs over that same WinRM path, so it IS the real
    // reachability signal: if it returns, we expand; if it throws, we surface it.
    set({ loadingExpansion: { ...get().loadingExpansion, [serverName]: true } });
    try {
      if (!get().sharesCache[serverName]) {
        const list = await fetchSharesList(serverName);
        const shares = Array.isArray(list) ? list : list?.shares ?? [];
        set((s) => ({ sharesCache: { ...s.sharesCache, [serverName]: shares } }));
      }
      expanded.add(serverName);
      set({ expandedServers: expanded });
      return { expanded: true };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to load shares' };
    } finally {
      set((s) => {
        const next = { ...s.loadingExpansion };
        delete next[serverName];
        return { loadingExpansion: next };
      });
    }
  },

  /**
   * Force a live re-enumeration of one server's shares (bypasses the
   * backend memo via ?refresh=true). Used by the per-card "Refresh shares"
   * button so the operator can pull current shares on demand.
   */
  async refreshServerShares(serverName) {
    set({ loadingExpansion: { ...get().loadingExpansion, [serverName]: true } });
    try {
      const list = await fetchSharesList(serverName, true);
      const shares = Array.isArray(list) ? list : list?.shares ?? [];
      set((s) => ({ sharesCache: { ...s.sharesCache, [serverName]: shares } }));
      return { ok: true, count: shares.length };
    } catch (err) {
      return { error: err instanceof Error ? err.message : 'Failed to refresh shares' };
    } finally {
      set((s) => {
        const next = { ...s.loadingExpansion };
        delete next[serverName];
        return { loadingExpansion: next };
      });
    }
  },

  async loadDriveMappings() {
    if (get().driveMappingsLoaded) return;
    try {
      const data = await fetchAllDrives();
      set({ driveMappings: Array.isArray(data) ? data : [], driveMappingsLoaded: true });
    } catch {
      set({ driveMappings: [], driveMappingsLoaded: true });
    }
  },

  /* --------- permissions modal --------- */

  openPermissions(path) {
    set({
      permissionsModal: { open: true, path, permissions: [], loading: false, error: null }
    });
  },

  closePermissions() {
    set({
      permissionsModal: { open: false, path: null, permissions: [], loading: false, error: null }
    });
  },

  setPermissionsLoading(loading) {
    set((s) => ({ permissionsModal: { ...s.permissionsModal, loading } }));
  },

  setPermissions(permissions) {
    set((s) => ({
      permissionsModal: { ...s.permissionsModal, permissions, error: null, loading: false }
    }));
  },

  setPermissionsError(error) {
    set((s) => ({
      permissionsModal: { ...s.permissionsModal, error, loading: false }
    }));
  },

  /* --------- sessions modal --------- */

  openSessions(server) {
    set({ sessionsModal: { open: true, server, sessions: [], loading: true, error: null } });
  },
  closeSessions() {
    set({ sessionsModal: { open: false, server: null, sessions: [], loading: false, error: null } });
  },
  setSessions(sessions) {
    set((s) => ({ sessionsModal: { ...s.sessionsModal, sessions, loading: false } }));
  },
  setSessionsError(error) {
    set((s) => ({ sessionsModal: { ...s.sessionsModal, error, loading: false } }));
  },

  /* --------- share wizard --------- */

  openWizard(seed = {}) {
    set({ wizardOpen: true, wizard: { ...WIZARD_INITIAL, ...seed } });
  },
  closeWizard() {
    set({ wizardOpen: false, wizard: { ...WIZARD_INITIAL } });
  },
  patchWizard(patch) {
    set((s) => ({ wizard: { ...s.wizard, ...patch } }));
  },
  setWizardStep(step) {
    set((s) => ({ wizard: { ...s.wizard, currentStep: step } }));
  }
}));

/**
 * Canonicalize a UNC path for comparison: lowercase, trailing slashes
 * stripped, and the server segment reduced to its SHORT name. Drive
 * mappings store FQDN paths (\\gg-fs01.ballyvesey.com\accounts) while
 * the share cache uses short server names (\\GG-FS01\Accounts) — raw
 * string comparison never matches (monolith normalised to FQDN at
 * line 19727; short-name reduction is equivalent and domain-agnostic).
 *
 * @param {string} uncPath
 */
function canonicalUnc(uncPath) {
  const text = String(uncPath || '').trim().replace(/\\+$/, '').toLowerCase();
  const match = text.match(/^\\\\([^\\]+)(.*)$/);
  if (!match) return text;
  const shortServer = match[1].split('.')[0];
  return `\\\\${shortServer}${match[2]}`;
}

/**
 * Find drive mappings that target a given share path.
 * Returns array of { drive, label, type: 'exact' | 'subfolder' }.
 * Matches the monolith's three cases: exact, drive maps a subfolder of
 * the share, and drive maps a parent folder of the share.
 *
 * @param {Array<object>} driveMappings
 * @param {string} sharePath
 */
export function findDriveMappingsForShare(driveMappings, sharePath) {
  if (!sharePath || !Array.isArray(driveMappings)) return [];
  const share = canonicalUnc(sharePath);
  if (!share) return [];
  // Dedupe — the drive config can hold several entries for the same
  // mapping that differ only by group filter.
  const seen = new Set();
  const out = [];
  for (const dm of driveMappings) {
    const target = canonicalUnc(dm.Path ?? dm.path ?? '');
    if (!target) continue;
    const entry = {
      drive: dm.DriveLetter ?? dm.driveLetter ?? '?',
      label: dm.Label ?? dm.label ?? ''
    };
    let type = null;
    if (target === share) type = 'exact';
    else if (target.startsWith(share + '\\') || share.startsWith(target + '\\')) type = 'subfolder';
    if (!type) continue;
    const key = `${entry.drive}|${entry.label}|${type}`.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ ...entry, type });
  }
  return out;
}
